import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { AsyncPipe } from '@angular/common';

import { Dialog } from '@angular/cdk/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs';

import {
  AnnouncementDetailsBaseComponent,
  BreadcrumbItem,
  IAnnouncementsState,
  IContentAttachment,
  ImmomioIconName,
  IsPdfPipe,
  isPdf,
} from '@resident-nx/shared';
import { ROUTE } from '../../../models';
import { FileDownloadWebService } from '../../../services';
import { DateTransformPipe } from '../../../pipes/date.pipe';
import {
  CardWebComponent,
  Elevation,
  ElevationDirective,
  FilePreviewModalComponent,
  FilePreviewModalData,
  FilePreviewModalFile,
  IconWebComponent,
  LoaderWebComponent,
} from '@resident-nx/web/ui';
import { BreadcrumbsWebComponent } from '../../atoms/breadcrumbs/breadcrumbs.component';

@Component({
  standalone: true,
  selector: 'rs-web-announcement-details',
  templateUrl: './announcement-details.component.html',
  styleUrls: ['./announcement-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BreadcrumbsWebComponent,
    ElevationDirective,
    IconWebComponent,
    LoaderWebComponent,
    AsyncPipe,
    DateTransformPipe,
    CardWebComponent,
    IsPdfPipe,
  ],
})
export class AnnouncementDetailsWebComponent
  extends AnnouncementDetailsBaseComponent
  implements OnInit, OnDestroy
{
  #destroyRef = inject(DestroyRef);
  #router = inject(Router);
  #downloadService = inject(FileDownloadWebService);
  #dialog = inject(Dialog);

  protected readonly immomioIconName = ImmomioIconName;
  public readonly elevation = Elevation;
  public breadcrumbs: BreadcrumbItem[] = [
    {
      label: 'main_navigation_news_l',
      url: ROUTE.NEWS,
    },
    {
      label: 'news.announcements_l',
      url: `${ROUTE.NEWS}/${ROUTE.ANNOUNCEMENTS}`,
    },
  ];

  constructor(private store: Store<IAnnouncementsState>) {
    super(store);
  }

  private onPreview(contentId: string, attachment: IContentAttachment): void {
    const file: FilePreviewModalFile = {
      identifier: attachment.id,
      name: attachment.fileName,
    };

    const data: FilePreviewModalData = {
      files: [file],
    };

    const dialogRef = this.#dialog.open(FilePreviewModalComponent, {
      data,
    });

    const modalComponent = dialogRef.componentInstance as FilePreviewModalComponent;

    modalComponent.download.subscribe(fileToDownload =>
      this.download(fileToDownload.downloadUrl, fileToDownload.name)
    );

    this.#downloadService
      .downloadContentAttachment(contentId, attachment.id, attachment.extension)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: fileData => {
          const updatedFile: FilePreviewModalFile = {
            ...file,
            fileUrl: fileData.preview,
            downloadUrl: fileData.download,
            extension: fileData.extension,
            isRequested: false,
            isAlreadyRequested: false,
          };
          modalComponent.files.set([updatedFile]);
        },
        error: (error: Error) => {
          console.error(error);
          dialogRef.close();
        },
      });
  }

  private download(url: string, name: string): void {
    this.#downloadService.download(url, name);
  }

  ngOnInit(): void {
    this.announcement$
      .pipe(
        filter(a => a !== null),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(announcement => {
        this.breadcrumbs.push({ label: announcement.title });
      });
  }

  ngOnDestroy(): void {
    this.#dialog.closeAll();
  }

  public onNavigateBack() {
    void this.#router.navigate([ROUTE.NEWS, ROUTE.ANNOUNCEMENTS]);
  }

  public onNavigate(event: string) {
    void this.#router.navigate([event]);
  }

  public onAttachmentClick(contentId: string, attachment: IContentAttachment): void {
    if (isPdf(attachment.extension)) {
      this.#downloadService
        .downloadContentAttachment(contentId, attachment.id, attachment.extension)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: fileData => this.download(fileData.download, attachment.fileName),
          error: (error: Error) => console.error(error),
        });
    } else {
      this.onPreview(contentId, attachment);
    }
  }
}
